import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, CircleCheck, Info, RotateCcw, TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingButton } from '@components/common/LoadingButton';
import SectionCard from './SectionCard';

export interface MetadataInfo {
  document_count: number;
  default_available: boolean;
  metadata_file: string;
}

interface DataManagementCardProps {
  metadataInfo: MetadataInfo | null;
  resetting: boolean;
  result: { type: 'success' | 'error'; text: string } | null;
  onResetClick: () => void;
}

const DataManagementCard: React.FC<DataManagementCardProps> = ({
  metadataInfo,
  resetting,
  result,
  onResetClick,
}) => {
  const { t } = useTranslation();

  return (
    <SectionCard icon={RotateCcw} title={t('settings.dataManagement')}>
      {metadataInfo && (
        <div className="mb-6">
          <h3 className="revy-cjk mb-2 text-xs font-semibold leading-[1.5] tracking-[0.02em] text-muted-foreground">
            {t('settings.currentStatus')}
          </h3>
          <div className="space-y-1 pl-4">
            <p className="revy-cjk revy-figure text-sm leading-[1.6]">
              {t('settings.registeredDocuments', { count: metadataInfo.document_count })}
            </p>
            <p className="revy-cjk text-sm leading-[1.6]">
              {t('settings.defaultBackup', {
                status: metadataInfo.default_available
                  ? t('settings.available')
                  : t('settings.unavailable'),
              })}
            </p>
          </div>
        </div>
      )}

      <Alert variant="info" className="mb-4">
        <Info className="h-4 w-4" />
        <AlertDescription className="revy-cjk text-sm leading-[1.7]">
          <strong>{t('settings.dataResetTitle')}</strong>
          <br />
          {t('settings.dataResetDescription')}
        </AlertDescription>
      </Alert>

      <Alert variant="warning" className="mb-4">
        <TriangleAlert className="h-4 w-4" />
        <AlertDescription className="revy-cjk text-sm leading-[1.7]">
          <strong>{t('settings.warningLabel')}:</strong> {t('settings.dataResetWarning')}
        </AlertDescription>
      </Alert>

      {result && (
        <Alert variant={result.type === 'success' ? 'success' : 'destructive'} className="mb-4">
          {result.type === 'success' ? (
            <CircleCheck className="h-4 w-4" />
          ) : (
            <CircleAlert className="h-4 w-4" />
          )}
          <AlertDescription className="revy-cjk leading-[1.6]">{result.text}</AlertDescription>
        </Alert>
      )}

      <LoadingButton
        loading={resetting}
        loadingText={t('settings.resetting')}
        onClick={onResetClick}
        disabled={!metadataInfo?.default_available}
        className="mt-4 w-full bg-warning text-warning-foreground hover:bg-warning/90"
      >
        <RotateCcw strokeWidth={1.9} />
        <span className="revy-cjk">{t('settings.resetToDefault')}</span>
      </LoadingButton>
    </SectionCard>
  );
};

export default DataManagementCard;
